'use client';

import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { CitingRegulation } from '@/lib/enabling-clause';
import { ArticleReferenceText } from '@/components/ui/ArticleReferenceText';
import { LampiranReferenceText, type LampiranClickHandler } from '@/components/ui/LampiranReferenceText';
import { TextHighlighter } from '@/components/ui/TextHighlighter';
import { cn } from '@/lib/utils';
import { detectMarkdownTable } from '@/lib/table-detector';

interface LegalTextFormatterProps {
  text: string;
  regulationId?: string;
  consolidationId?: string;
  query?: string;
  highlightClassName?: string;
  className?: string;
  enableArticleReferences?: boolean;
  itemStatuses?: Record<string, 'modified' | 'added' | 'removed'>;
  citingRegulations?: CitingRegulation[];
  hasPremiumRole?: boolean;
  onLampiranClick?: LampiranClickHandler;
}

export type MarkerType =
  | 'paren-number'
  | 'paren-letter'
  | 'paren-alphanum'
  | 'numbered'
  | 'lettered'
  | 'letter-paren'
  | 'bullet';

const LIST_PATTERNS: Record<MarkerType, RegExp> = {
  'paren-number': /^\((\d+)\)\s+/,
  'paren-letter': /^\(([a-zA-Z])\)\s+/,
  'paren-alphanum': /^\((\d+[a-zA-Z])\)\s+/,
  numbered: /^(\d+)\.\s+/,
  lettered: /^([a-zA-Z])\.\s+/,
  'letter-paren': /^([a-zA-Z])\)\s+/,
  bullet: /^[-•]\s+/,
};

const DETECTION_ORDER: MarkerType[] = [
  'paren-alphanum',
  'paren-number',
  'paren-letter',
  'numbered',
  'lettered',
  'letter-paren',
  'bullet',
];
function splitTextAtListMarkers(text: string): string {
  let result = text;

  result = result.replace(/(^|\n) \((\d+[a-zA-Z])\) ([A-Z])/g, '$1\n($2) $3');
  result = result.replace(/(^|\n) \((\d+)\) ([A-Z])/g, '$1\n($2) $3');
  result = result.replace(/(^|\n) \(([a-zA-Z])\) ([a-z])/g, '$1\n($2) $3');
  result = result.replace(/\. (\d+)\. /g, '.\n$1. ');
  result = result.replace(/\. ([a-zA-Z])\. /g, '.\n$1. ');
  result = result.replace(/\. ([a-zA-Z])\) /g, '.\n$1) ');

  return result;
}

export interface ListItem {
  marker: string;
  markerType: MarkerType;
  content: string;
  level: number;
  id: string;
}

export interface TextSegment {
  type: 'text' | 'list' | 'table';
  content: string;
  listItems?: ListItem[];
  id?: string;
}

function detectListMarker(
  line: string
): { marker: string; type: MarkerType; content: string } | null {
  const trimmed = line.trimStart();

  for (const type of DETECTION_ORDER) {
    const pattern = LIST_PATTERNS[type];
    const match = trimmed.match(pattern);
    if (match) {
      const marker = match[1] || '';
      const content = trimmed.slice(match[0].length);
      return { marker, type, content };
    }
  }

  return null;
}

function collapseSingleNewlines(text: string): string {
  return text.replace(/(?<!\n)\n(?!\n)/g, ' ');
}

export function parseTextIntoSegments(text: string): TextSegment[] {
  const normalizedText = splitTextAtListMarkers(text);
  const lines = normalizedText.split('\n');
  const segments: TextSegment[] = [];

  let currentTextLines: string[] = [];
  let currentListItems: ListItem[] = [];
  let currentItem: ListItem | null = null;
  let inList = false;
  let currentPath: Array<{ type: MarkerType; marker: string }> = [];
  let textSegmentCount = 0;
  let shouldNest = false;

  const flushText = () => {
    if (currentTextLines.length > 0) {
      textSegmentCount++;
      const rawContent = currentTextLines.join('\n').trim();
      const content = collapseSingleNewlines(rawContent);
      segments.push({
        type: 'text',
        content,
        id: `text-${textSegmentCount}`,
      });
      currentTextLines = [];
    }
  };

  const flushList = () => {
    if (currentListItems.length > 0) {
      segments.push({
        type: 'list',
        content: '',
        listItems: currentListItems,
        id: `list-${currentListItems[0].id}`,
      });
      currentListItems = [];
    }
    currentItem = null;
    inList = false;
    currentPath = [];
    shouldNest = false;
  };

  const normalizeType = (markerType: MarkerType): MarkerType => {
    return markerType === 'paren-alphanum' ? 'paren-number' : markerType;
  };

  const getLevelForMarker = (markerType: MarkerType): number => {
    const normalizedType = normalizeType(markerType);

    if (shouldNest && currentPath.length > 0) {
      const currentLevelType = normalizeType(currentPath[currentPath.length - 1].type);
      if (currentLevelType !== normalizedType) {
        return currentPath.length;
      }
    }

    for (let i = currentPath.length - 1; i >= 0; i--) {
      if (normalizeType(currentPath[i].type) === normalizedType) {
        return i;
      }
    }

    return currentPath.length;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      if (currentItem) {
        currentItem.content += '\n\n';
      } else if (currentTextLines.length > 0 && !inList) {
        currentTextLines.push('');
      }
      continue;
    }

    const tableDetection = detectMarkdownTable(lines, i);
    if (tableDetection) {
      if (inList) {
        flushList();
      }
      if (currentTextLines.length > 0) {
        flushText();
      }
      
      segments.push({
        type: 'table',
        content: tableDetection.tableText,
        id: `table-${i}`,
      });
      
      i = tableDetection.endIndex;
      continue;
    }

    const listMatch = detectListMarker(line);

    if (listMatch) {
      if (!inList && currentTextLines.length > 0) {
        flushText();
      }
      inList = true;

      if (currentItem) {
        currentItem.content = currentItem.content.trim();
      }

      const level = getLevelForMarker(listMatch.type);
      currentPath = currentPath.slice(0, level);
      currentPath.push({ type: listMatch.type, marker: listMatch.marker });
      const id = currentPath.map((p) => p.marker).join('.');

      currentItem = {
        marker: listMatch.marker,
        markerType: listMatch.type,
        content: listMatch.content,
        level,
        id,
      };
      currentListItems.push(currentItem);

      shouldNest = listMatch.content.trimEnd().endsWith(':');

      continue;
    }

    if (currentItem && inList) {
      currentItem.content += ' ' + trimmedLine;
      shouldNest = currentItem.content.trimEnd().endsWith(':');
      continue;
    }

    if (inList) {
      flushList();
    }

    currentTextLines.push(line);
  }

  if (currentItem) {
    currentItem.content = currentItem.content.trim();
  }

  flushList();
  flushText();

  return segments;
}

interface ListRendererProps {
  items: ListItem[];
  regulationId?: string;
  consolidationId?: string;
  query?: string;
  highlightClassName?: string;
  enableArticleReferences?: boolean;
  itemStatuses?: Record<string, 'modified' | 'added' | 'removed'>;
  citingRegulations?: CitingRegulation[];
  hasPremiumRole?: boolean;
  onLampiranClick?: LampiranClickHandler;
}

function ListRenderer({
  items,
  regulationId,
  consolidationId,
  query,
  highlightClassName,
  enableArticleReferences = true,
  itemStatuses,
  citingRegulations,
  hasPremiumRole,
  onLampiranClick,
}: ListRendererProps) {
  return (
    <div className="space-y-1">
      {items.map((item, idx) => {
        let markerDisplay = '';
        switch (item.markerType) {
          case 'numbered':
            markerDisplay = `${item.marker}.`;
            break;
          case 'lettered':
            markerDisplay = `${item.marker}.`;
            break;
          case 'letter-paren':
            markerDisplay = `${item.marker})`;
            break;
          case 'paren-number':
            markerDisplay = `(${item.marker})`;
            break;
          case 'paren-letter':
            markerDisplay = `(${item.marker})`;
            break;
          case 'paren-alphanum':
            markerDisplay = `(${item.marker})`;
            break;
          case 'bullet':
            markerDisplay = '•';
            break;
        }

        const indentStyle = getIndentStyle(item.level);
        const status = itemStatuses?.[item.id];
        const isModified = status === 'modified';
        const isAdded = status === 'added';
        const isRemoved = status === 'removed';

        return (
          <div
            key={idx}
            className={cn(
              'flex gap-2 transition-colors duration-200 rounded-sm -mx-2 px-2 py-1',
              isModified && 'bg-amber-100/50 border-l-2 border-amber-400',
              isAdded && 'bg-green-100/50 border-l-2 border-green-400',
              isRemoved && 'bg-red-100/50 border-l-2 border-red-400 opacity-70 line-through'
            )}
            style={indentStyle}
          >
            <span className="font-medium text-gray-700 flex-shrink-0 select-none min-w-[1.5rem]">
              {markerDisplay}
            </span>
            <div className="flex-1 text-justify">
              {enableArticleReferences && regulationId ? (
                <ArticleReferenceText
                  text={item.content}
                  regulationId={regulationId}
                  consolidationId={consolidationId}
                  query={query}
                  highlightClassName={highlightClassName}
                  citingRegulations={citingRegulations}
                  hasPremiumRole={hasPremiumRole}
                />
              ) : onLampiranClick ? (
                <LampiranReferenceText
                  text={item.content}
                  onLampiranClick={onLampiranClick}
                />
              ) : query ? (
                <TextHighlighter
                  text={item.content}
                  query={query}
                  highlightClassName={highlightClassName}
                />
              ) : (
                <span className="whitespace-pre-wrap">{item.content}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function getIndentStyle(level: number): React.CSSProperties {
  const indentRem = level === 0 ? 0 : level * 1.5 + 1;
  return { marginLeft: `${indentRem}rem` };
}

export function LegalTextFormatter({
  text,
  regulationId,
  consolidationId,
  query,
  highlightClassName,
  className,
  enableArticleReferences = true,
  itemStatuses,
  citingRegulations,
  hasPremiumRole,
  onLampiranClick,
}: LegalTextFormatterProps) {
  const segments = useMemo(() => {
    if (!text) return [];
    return parseTextIntoSegments(text);
  }, [text]);

  if (!text) return null;

  return (
    <div className={cn('leading-relaxed tracking-tight', className)}>
      {segments.map((segment, idx) => {
        if (segment.type === 'table') {
          return (
            <div key={idx} className="mb-4 overflow-x-auto">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ children }) => (
                    <table className="min-w-full border-collapse border border-neutral-300 text-xs">
                      {children}
                    </table>
                  ),
                  thead: ({ children }) => (
                    <thead className="bg-neutral-100">{children}</thead>
                  ),
                  th: ({ children }) => (
                    <th className="border border-neutral-300 px-2 py-1 text-left font-semibold text-neutral-700">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="border border-neutral-300 px-2 py-1 text-neutral-600">
                      {children}
                    </td>
                  ),
                  tr: ({ children }) => (
                    <tr className="even:bg-neutral-50">{children}</tr>
                  ),
                }}
              >
                {segment.content}
              </ReactMarkdown>
            </div>
          );
        }

        if (segment.type === 'list' && segment.listItems) {
          return (
            <div key={idx} className="mb-3">
              <ListRenderer
                items={segment.listItems}
                regulationId={regulationId}
                consolidationId={consolidationId}
                query={query}
                highlightClassName={highlightClassName}
                enableArticleReferences={enableArticleReferences}
                itemStatuses={itemStatuses}
                citingRegulations={citingRegulations}
                hasPremiumRole={hasPremiumRole}
                onLampiranClick={onLampiranClick}
              />
            </div>
          );
        }

        const isModified = segment.id && itemStatuses?.[segment.id] === 'modified';
        const isAdded = segment.id && itemStatuses?.[segment.id] === 'added';
        const isRemoved = segment.id && itemStatuses?.[segment.id] === 'removed';

        return (
          <div
            key={idx}
            className={cn(
              'mb-3 last:mb-0 whitespace-pre-wrap transition-colors duration-200 rounded-sm -mx-2 px-2 py-1',
              isModified && 'bg-amber-100/50 border-l-2 border-amber-400',
              isAdded && 'bg-green-100/50 border-l-2 border-green-400',
              isRemoved && 'bg-red-100/50 border-l-2 border-red-400 opacity-70 line-through'
            )}
          >
            {enableArticleReferences && regulationId ? (
              <ArticleReferenceText
                text={segment.content}
                regulationId={regulationId}
                consolidationId={consolidationId}
                query={query}
                highlightClassName={highlightClassName}
                citingRegulations={citingRegulations}
                hasPremiumRole={hasPremiumRole}
              />
            ) : onLampiranClick ? (
              <LampiranReferenceText
                text={segment.content}
                onLampiranClick={onLampiranClick}
              />
            ) : query ? (
              <TextHighlighter
                text={segment.content}
                query={query}
                highlightClassName={highlightClassName}
              />
            ) : (
              segment.content
            )}
          </div>
        );
      })}
    </div>
  );
}

export function formatLegalTextSimple(text: string): React.ReactNode {
  if (!text) return null;

  const segments = parseTextIntoSegments(text);

  return (
    <div className="leading-relaxed tracking-tight">
      {segments.map((segment, idx) => {
        if (segment.type === 'table') {
          return (
            <div key={idx} className="mb-4 overflow-x-auto">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ children }) => (
                    <table className="min-w-full border-collapse border border-neutral-300 text-xs">
                      {children}
                    </table>
                  ),
                  thead: ({ children }) => (
                    <thead className="bg-neutral-100">{children}</thead>
                  ),
                  th: ({ children }) => (
                    <th className="border border-neutral-300 px-2 py-1 text-left font-semibold text-neutral-700">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="border border-neutral-300 px-2 py-1 text-neutral-600">
                      {children}
                    </td>
                  ),
                  tr: ({ children }) => (
                    <tr className="even:bg-neutral-50">{children}</tr>
                  ),
                }}
              >
                {segment.content}
              </ReactMarkdown>
            </div>
          );
        }

        if (segment.type === 'list' && segment.listItems) {
          return (
            <ListRenderer key={idx} items={segment.listItems} enableArticleReferences={false} />
          );
        }

        return (
          <div key={idx} className="mb-3 last:mb-0 whitespace-pre-wrap">
            {segment.content}
          </div>
        );
      })}
    </div>
  );
}

export function formatLegalTextForClipboard(
  articleNumber: string,
  content: string
): { html: string; plainText: string } {
  const segments = parseTextIntoSegments(content);

  const htmlParts: string[] = [];
  const plainTextParts: string[] = [];

  htmlParts.push(
    `<p style="font-family: 'Times New Roman', serif; font-size: 12pt; font-weight: bold; margin-bottom: 12pt;">Pasal ${articleNumber}</p>`
  );
  plainTextParts.push(`Pasal ${articleNumber}`);

  for (const segment of segments) {
    if (segment.type === 'list' && segment.listItems) {
      htmlParts.push('<div style="margin-bottom: 6pt;">');

      for (const item of segment.listItems) {
        let markerDisplay = '';
        switch (item.markerType) {
          case 'numbered':
            markerDisplay = `${item.marker}.`;
            break;
          case 'lettered':
            markerDisplay = `${item.marker}.`;
            break;
          case 'letter-paren':
            markerDisplay = `${item.marker})`;
            break;
          case 'paren-number':
            markerDisplay = `(${item.marker})`;
            break;
          case 'paren-letter':
            markerDisplay = `(${item.marker})`;
            break;
          case 'paren-alphanum':
            markerDisplay = `(${item.marker})`;
            break;
          case 'bullet':
            markerDisplay = '•';
            break;
        }

        const indentEm = item.level === 0 ? 0 : item.level * 1.5 + 1;
        const indentStyle = indentEm > 0 ? `margin-left: ${indentEm}em; ` : '';

        htmlParts.push(
          `<div style="${indentStyle}margin-bottom: 4pt; font-family: 'Times New Roman', serif; font-size: 12pt; text-align: justify;"><span style="font-weight: 500;">${markerDisplay}</span> ${item.content}</div>`
        );
        plainTextParts.push(`${'  '.repeat(item.level)}${markerDisplay} ${item.content}`);
      }

      htmlParts.push('</div>');
    } else if (segment.content) {
      htmlParts.push(
        `<p style="font-family: 'Times New Roman', serif; font-size: 12pt; margin-bottom: 6pt; text-align: justify; line-height: 1.5;">${segment.content}</p>`
      );
      plainTextParts.push(segment.content);
    }
  }

  return {
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>${htmlParts.join('\n')}</body></html>`,
    plainText: plainTextParts.join('\n\n'),
  };
}
